import { ITEM_SERIES_NO_MANUFACTURER, Size, t, type ItemSeriesOption } from '@bms2/shared';
import { Select } from '../../components/controls';
import { PAGES_PATH } from './api';

/** The Series menu's options: each `series` value an Item holds, read once (decision 7640). */
export const ITEM_SERIES_PATH = `${PAGES_PATH}/item-series`;

/** The menu's choice as one value: the series, then the manufacturer where two manufacturers hold the same series. */
const PARTS_SEPARATOR = '\u0001';

export interface ItemSeriesChoice {
  series: string;
  manufacturer: string;
}

/**
 * 🔒 The Items list's Series menu (decision 7640; T3125): each `series` value an
 * Item holds, so a manufacturer's product line is ticked in one press. The
 * manufacturer's name stands beside a series two manufacturers both hold, and
 * the choice then carries the manufacturer too, which is what narrows it.
 */
export function ItemSeriesFilter({ options, value, onChange }: { options: readonly ItemSeriesOption[]; value: ItemSeriesChoice; onChange: (next: ItemSeriesChoice) => void }) {
  const holders = new Map<string, number>();
  for (const option of options) holders.set(option.series, (holders.get(option.series) ?? 0) + 1);
  const shared = (option: ItemSeriesOption) => (holders.get(option.series) ?? 0) > 1;
  // 🔒 A shared series held with no manufacturer names that, never an empty manufacturer, which reads as every one (decision 7871).
  const keyOf = (option: ItemSeriesOption) => (shared(option) ? [option.series, option.manufacturerCompanyId ?? ITEM_SERIES_NO_MANUFACTURER].join(PARTS_SEPARATOR) : option.series);
  const entries = options.map((option) => ({
    value: keyOf(option),
    label: `${option.series}${shared(option) ? ` · ${option.manufacturerName ?? t('item.list.noManufacturer')}` : ''} (${option.count})`,
  }));
  const current = value.series === '' ? '' : value.manufacturer === '' ? value.series : [value.series, value.manufacturer].join(PARTS_SEPARATOR);
  // A choice the menu no longer offers, such as a series the last Item of which was edited away, still shows rather than hiding itself.
  const shown = current !== '' && !entries.some((entry) => entry.value === current) ? [...entries, { value: current, label: value.series }] : entries;
  return (
    <div data-list-series-filter="true">
      <Select
        size={Size.Medium}
        label={t('item.list.series')}
        value={current}
        placeholder={t('item.list.allSeries')}
        options={shown}
        onChange={(next) => {
          const [series = '', manufacturer = ''] = next.split(PARTS_SEPARATOR);
          onChange({ series, manufacturer });
        }}
        dataAttribute={{ 'data-series-filter': 'select' }}
      />
    </div>
  );
}
